/**
 * LeadInfoPane.js — Lead Information tab in agent conversation panel.
 * Fetches GHL contact data, caches to DB, allows agent edits with highlights.
 * Supports adding/deleting dependents and a spouse block.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

function authHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('agentToken')}` } };
}

const GROUP_META = {
  'Personal Info': { icon: '👤', order: 0 },
  'Dependents':    { icon: '👨‍👩‍👧', order: 1 },
  'Spouse':        { icon: '💍', order: 2 },
  'Plan 1':        { icon: '📋', order: 3 },
  'Plan 2':        { icon: '📋', order: 4 },
  'Plan 3':        { icon: '📋', order: 5 },
  'Medicare':      { icon: '🏥', order: 6 },
  'Agent Info':    { icon: '🔒', order: 7 },
  'Status':        { icon: '📊', order: 8 },
  'Notes':         { icon: '📝', order: 9 },
  'Consent & Signatures': { icon: '✍️', order: 10 },
  'Other Details': { icon: '🔧', order: 11 },
};

function groupFields(fields) {
  const groups = {};
  for (const f of fields) {
    const g = f.group || 'Other Details';
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  }
  return Object.entries(groups).sort(([a], [b]) => {
    return (GROUP_META[a]?.order ?? 99) - (GROUP_META[b]?.order ?? 99);
  });
}

function isUrl(val) {
  return typeof val === 'string' && (val.startsWith('http://') || val.startsWith('https://'));
}

// ── FieldRow ──────────────────────────────────────────────────────────────────
function FieldRow({ field, originalField, onChange, isModified }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const ACCENT = '#059669';
  const isSensitive = field.sensitive === true;

  const border = isModified ? '#f59e0b' : '#e5e7eb';
  const bg     = field.readOnly ? '#f9fafb' : isModified ? '#fffbeb' : '#fff';

  const renderValue = (val) => {
    if (!val) return <span style={{ color: '#9ca3af' }}>—</span>;
    if (isUrl(val)) return (
      <a href={val} target="_blank" rel="noopener noreferrer"
         style={{ color: ACCENT, textDecoration: 'underline', wordBreak: 'break-all' }}>
        {val}
      </a>
    );
    return <span style={{ wordBreak: 'break-word' }}>{val}</span>;
  };

  // Long labels (consent/signature text) get a different treatment — shown as body text, not uppercase caps
  const isLongLabel = field.fieldLabel && field.fieldLabel.length > 60;

  return (
    <div style={{ marginBottom: '14px' }}>
      {/* Label row */}
      {isLongLabel ? (
        <div style={{ marginBottom: '6px', padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#059669', flexShrink: 0 }}>✅</span>
            <p style={{ margin: 0, fontSize: '12px', color: '#166534', lineHeight: '1.5', fontStyle: 'italic' }}>
              {field.fieldLabel}
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {field.fieldLabel}
          </label>
          {field.readOnly && <span style={{ fontSize: '10px', color: '#9ca3af' }}>🔒</span>}
          {isModified && (
            <span style={{ fontSize: '10px', background: '#fef3c7', color: '#d97706', borderRadius: '999px', padding: '1px 7px', fontWeight: 700 }}>
              edited
            </span>
          )}
        </div>
      )}

      {/* Value */}
      {field.readOnly ? (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#374151', minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ flex: 1 }}>
            {isSensitive && !revealed
              ? <span style={{ color: '#9ca3af', letterSpacing: '3px' }}>••••••••</span>
              : renderValue(field.value)
            }
          </span>
          {isSensitive && (
            <button onClick={() => setRevealed(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#9ca3af', padding: 0, flexShrink: 0 }}>
              {revealed ? '🙈' : '👁️'}
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            type={isSensitive && !revealed ? 'password' : 'text'}
            value={field.value || ''}
            onChange={e => onChange(field.fieldKey, e.target.value)}
            style={{ flex: 1, background: bg, border: `1.5px solid ${border}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s', minWidth: 0 }}
            onFocus={e => { e.target.style.borderColor = isModified ? '#f59e0b' : ACCENT; }}
            onBlur={e => { e.target.style.borderColor = border; }}
          />
          {isSensitive && (
            <button onClick={() => setRevealed(p => !p)} title={revealed ? 'Hide' : 'Reveal'} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', color: '#9ca3af', padding: '7px 9px', flexShrink: 0 }}>
              {revealed ? '🙈' : '👁️'}
            </button>
          )}
          {!isSensitive && isUrl(field.value) && (
            <a href={field.value} target="_blank" rel="noopener noreferrer" title="Open URL" style={{ color: ACCENT, fontSize: '16px', textDecoration: 'none', flexShrink: 0 }}>🔗</a>
          )}
        </div>
      )}

      {/* View original */}
      {isModified && !field.readOnly && originalField && (
        <div style={{ marginTop: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => setShowOriginal(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#d97706', padding: 0, textDecoration: 'underline' }}>
            {showOriginal ? 'Hide original' : 'View original'}
          </button>
          {showOriginal && (
            <span style={{ fontSize: '11.5px', color: '#6b7280', background: '#f3f4f6', borderRadius: '5px', padding: '2px 8px' }}>
              {originalField.value || '—'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Dependent / Spouse helpers ────────────────────────────────────────────────
function depFields(n) {
  return [
    { key: `dependent${n}FirstName`, label: `Dep ${n} First Name` },
    { key: `dependent${n}LastName`,  label: `Dep ${n} Last Name` },
    { key: `dependent${n}Dob`,       label: `Dep ${n} DOB` },
    { key: `dependent${n}Gender`,    label: `Dep ${n} Gender` },
    { key: `dependent${n}Ssn`,       label: `Dep ${n} SSN`, sensitive: true },
  ];
}
const SPOUSE_FIELDS = [
  { key: 'spouseFirstName', label: 'Spouse First Name' },
  { key: 'spouseLastName',  label: 'Spouse Last Name' },
  { key: 'spouseDob',       label: 'Spouse DOB' },
  { key: 'spouseGender',    label: 'Spouse Gender' },
  { key: 'spouseSsn',       label: 'Spouse SSN', sensitive: true },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function LeadInfoPane({ leadEmail, insuranceType }) {
  const [state, setState]             = useState('idle');
  const [fields, setFields]           = useState([]);
  const [originalMap, setOriginalMap] = useState({});
  const [editMap, setEditMap]         = useState({});
  const [deletedKeys, setDeletedKeys] = useState(new Set());
  const [source, setSource]           = useState(null);
  const [cachedAt, setCachedAt]       = useState(null);
  const [updatedAt, setUpdatedAt]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState('');
  const [hasEdits, setHasEdits]       = useState(false);
  const saveTimer = useRef(null);
  const ACCENT = '#059669';
  const insType = (insuranceType || 'aca').toLowerCase();

  const loadData = useCallback((refresh = false) => {
    if (!leadEmail) return;
    setState('loading'); setFields([]); setEditMap({}); setDeletedKeys(new Set()); setHasEdits(false); setSaveMsg('');
    const url = `/api/agent/lead-info/${encodeURIComponent(leadEmail)}?insuranceType=${insType}${refresh ? '&refresh=1' : ''}`;
    axios.get(url, authHeaders())
      .then(r => {
        const d = r.data;
        if (!d.success || !d.fields?.length) { setState('not_found'); return; }
        setFields(d.fields);
        const oMap = {};
        (d.originalData?.fields || d.fields).forEach(f => { oMap[f.fieldKey] = f; });
        setOriginalMap(oMap);
        if (d.updatedData?.fields) {
          const eMap = {};
          d.updatedData.fields.forEach(f => { if (oMap[f.fieldKey] && f.value !== oMap[f.fieldKey].value) eMap[f.fieldKey] = f.value; });
          setEditMap(eMap); setHasEdits(Object.keys(eMap).length > 0);
        }
        setSource(d.source); setCachedAt(d.cachedAt); setUpdatedAt(d.updatedAt); setState('loaded');
      })
      .catch(() => setState('error'));
  }, [leadEmail, insType]);

  useEffect(() => { loadData(false); }, [loadData]);

  const doSave = useCallback(async (extraEdit = {}, extraDeleted = null) => {
    setSaving(true);
    try {
      const m = { ...editMap, ...extraEdit };
      const del = extraDeleted || deletedKeys;

      // Build label lookup from current fields
      const labelByKey = {};
      fields.forEach(f => { labelByKey[f.fieldKey] = f.fieldLabel; });

      // Compute change log entries
      const changes = [];
      // Edited / added fields
      Object.entries(m).forEach(([k, newVal]) => {
        const origField = originalMap[k];
        const oldVal = origField?.value ?? '';
        if (newVal !== oldVal) {
          const type = origField ? 'edit' : 'add';
          changes.push({ fieldKey: k, fieldLabel: labelByKey[k] || k, oldValue: oldVal, newValue: newVal, type });
        }
      });
      // Deleted fields
      del.forEach(k => {
        const origField = originalMap[k];
        if (origField) {
          changes.push({ fieldKey: k, fieldLabel: labelByKey[k] || k, oldValue: origField.value ?? '', newValue: '', type: 'delete' });
        }
      });

      // Base fields (from GHL/cache), minus deleted, with edits applied
      const mf = fields
        .filter(f => !del.has(f.fieldKey))
        .map(f => ({ ...f, value: m[f.fieldKey] !== undefined ? m[f.fieldKey] : f.value }));
      // Append any brand-new fields (in editMap but not in original fields)
      const origKeys = new Set(fields.map(f => f.fieldKey));
      Object.entries(m).forEach(([k, v]) => {
        if (!origKeys.has(k) && !del.has(k) && v !== undefined) {
          const grp = k.startsWith('spouse') ? 'Spouse' : k.startsWith('dependent') ? 'Dependents' : 'Other Details';
          mf.push({ fieldKey: k, fieldLabel: labelByKey[k] || k, group: grp, value: v, readOnly: false, source: 'agent' });
        }
      });

      await axios.put(
        `/api/agent/lead-info/${encodeURIComponent(leadEmail)}?insuranceType=${insType}`,
        { fields: mf, changes },
        authHeaders()
      );
      const now = new Date().toISOString();
      setUpdatedAt(now); setSaveMsg('✓ Saved'); setTimeout(() => setSaveMsg(''), 3000);
    } catch { setSaveMsg('⚠ Save failed'); }
    finally { setSaving(false); }
  }, [fields, editMap, deletedKeys, originalMap, leadEmail, insType]);

  const handleChange = useCallback((key, val) => {
    setEditMap(p => ({ ...p, [key]: val })); setHasEdits(true); setSaveMsg('');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave({ [key]: val }), 1500);
  }, [doSave]);

  // Delete a set of keys (remove dependent or spouse block)
  const handleDeleteKeys = useCallback((keys) => {
    if (!window.confirm('Remove this person from the record?')) return;
    const next = new Set([...deletedKeys, ...keys]);
    setDeletedKeys(next);
    setEditMap(p => { const n = { ...p }; keys.forEach(k => delete n[k]); return n; });
    setHasEdits(true); setSaveMsg('');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave({}, next), 1500);
  }, [deletedKeys, doSave]);

  // Add a new dependent slot (next available number)
  const handleAddDependent = useCallback(() => {
    const existing = new Set();
    fields.forEach(f => { const m = f.fieldKey.match(/^dependent(\d+)/); if (m) existing.add(Number(m[1])); });
    Object.keys(editMap).forEach(k => { const m = k.match(/^dependent(\d+)/); if (m) existing.add(Number(m[1])); });
    let n = 1; while (existing.has(n)) n++;
    const initEdit = {};
    depFields(n).forEach(f => { initEdit[f.key] = ''; });
    setEditMap(p => ({ ...p, ...initEdit }));
    setDeletedKeys(p => { const s = new Set(p); depFields(n).forEach(f => s.delete(f.key)); return s; });
    setHasEdits(true);
  }, [fields, editMap]);

  // Add spouse fields
  const handleAddSpouse = useCallback(() => {
    const initEdit = {};
    SPOUSE_FIELDS.forEach(f => { initEdit[f.key] = ''; });
    setEditMap(p => ({ ...p, ...initEdit }));
    setDeletedKeys(p => { const s = new Set(p); SPOUSE_FIELDS.forEach(f => s.delete(f.key)); return s; });
    setHasEdits(true);
  }, []);

  const handleReset = async () => {
    if (!window.confirm('Reset all edits to the original GHL data?')) return;
    await axios.delete(`/api/agent/lead-info/${encodeURIComponent(leadEmail)}/reset?insuranceType=${insType}`, authHeaders()).catch(() => {});
    setEditMap({}); setDeletedKeys(new Set()); setHasEdits(false); setUpdatedAt(null); setSource('cached');
    setSaveMsg('↺ Reset'); setTimeout(() => setSaveMsg(''), 3000);
  };

  const modifiedCount = Object.keys(editMap).filter(k => editMap[k] !== originalMap[k]?.value).length + deletedKeys.size;

  // Separate out dependents + spouse groups; render everything else normally
  const depAndSpouseKeys = new Set([
    ...Array.from({length:10},(_,i)=>depFields(i+1).map(f=>f.key)).flat(),
    ...SPOUSE_FIELDS.map(f=>f.key),
  ]);
  const regularFields = fields.filter(f => !depAndSpouseKeys.has(f.fieldKey));
  const grouped = groupFields(regularFields);

  if (state === 'loading') return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'200px', gap:'12px', color:'#9ca3af' }}>
      <div style={{ width:28, height:28, border:'3px solid #e5e7eb', borderTopColor:'#059669', borderRadius:'50%', animation:'li-spin 0.7s linear infinite' }} />
      <style>{`@keyframes li-spin{to{transform:rotate(360deg)}}`}</style>
      <span style={{ fontSize:'13px' }}>Loading lead information…</span>
    </div>
  );
  if (state === 'error') return (
    <div style={{ padding:'32px', textAlign:'center' }}>
      <div style={{ fontSize:'32px', marginBottom:'8px' }}>⚠️</div>
      <div style={{ fontWeight:600, color:'#ef4444' }}>Failed to load lead info</div>
      <div style={{ fontSize:'12px', color:'#9ca3af', marginTop:'4px' }}>Check server logs.</div>
    </div>
  );
  if (state === 'not_found') return (
    <div style={{ padding:'32px', textAlign:'center', color:'#9ca3af' }}>
      <div style={{ fontSize:'32px', marginBottom:'8px' }}>🔍</div>
      <div style={{ fontWeight:600, color:'#374151' }}>No GHL data found</div>
      <div style={{ fontSize:'12px', marginTop:'4px' }}>This lead may not have a GHL contact yet.</div>
    </div>
  );
  if (state === 'idle') return null;

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', borderBottom:'1px solid #e5e7eb', background:'#f9fafb', flexShrink:0, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:0 }}>
          {updatedAt && <span style={{ fontSize:'11.5px', color:'#d97706' }}>✏️ Edited {new Date(updatedAt).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>}
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center', flexShrink:0 }}>
          {modifiedCount > 0 && <span style={{ fontSize:'11px', background:'#fef3c7', color:'#d97706', borderRadius:'999px', padding:'3px 10px', fontWeight:700 }}>{modifiedCount} change{modifiedCount!==1?'s':''}</span>}
          {saveMsg && <span style={{ fontSize:'12px', fontWeight:600, color:saveMsg.startsWith('✓')?'#059669':'#6b7280' }}>{saveMsg}</span>}
          <button onClick={()=>loadData(true)} title="Re-fetch fresh data from GHL" style={{ background:'#fff', color:'#6b7280', border:'1px solid #d1d5db', borderRadius:'7px', padding:'5px 10px', fontSize:'12px', cursor:'pointer' }}>🔄</button>
          {hasEdits && <>
            <button onClick={()=>doSave()} disabled={saving} style={{ background:'#059669', color:'#fff', border:'none', borderRadius:'7px', padding:'5px 14px', fontSize:'12px', fontWeight:600, cursor:saving?'wait':'pointer', opacity:saving?0.7:1 }}>
              {saving?'Saving…':'💾 Save'}
            </button>
            <button onClick={handleReset} style={{ background:'#fff', color:'#6b7280', border:'1px solid #d1d5db', borderRadius:'7px', padding:'5px 12px', fontSize:'12px', cursor:'pointer' }}>↺ Reset</button>
          </>}
        </div>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
        {grouped.map(([gName, gFields]) => (
          <React.Fragment key={gName}>
            <GroupSection groupName={gName} fields={gFields} originalMap={originalMap} editMap={editMap} onChange={handleChange} />
            {gName === 'Personal Info' && (<>
              <DependentsSection allFields={fields} editMap={editMap} deletedKeys={deletedKeys} onChange={handleChange} onDelete={handleDeleteKeys} onAddDependent={handleAddDependent} />
              <SpouseSection allFields={fields} editMap={editMap} deletedKeys={deletedKeys} onChange={handleChange} onDelete={handleDeleteKeys} onAddSpouse={handleAddSpouse} />
            </>)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function GroupSection({ groupName, fields, originalMap, editMap, onChange }) {
  const [collapsed, setCollapsed] = useState(false);
  const meta = GROUP_META[groupName] || { icon: '📁' };
  const modCount = fields.filter(f => !f.readOnly && editMap[f.fieldKey] !== undefined && editMap[f.fieldKey] !== (originalMap[f.fieldKey]?.value)).length;
  // Consent & Signatures use full-width single column (labels are long paragraph text)
  const isConsentGroup = groupName === 'Consent & Signatures';
  return (
    <div style={{ marginBottom:'22px' }}>
      <button onClick={()=>setCollapsed(p=>!p)} style={{ display:'flex', alignItems:'center', gap:'8px', width:'100%', background:'none', border:'none', cursor:'pointer', padding:'5px 0 8px', borderBottom:'2px solid #f3f4f6', marginBottom:'12px' }}>
        <span style={{ fontSize:'14px' }}>{meta.icon}</span>
        <span style={{ fontSize:'11px', fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em', flex:1, textAlign:'left' }}>{groupName}</span>
        {modCount > 0 && <span style={{ fontSize:'10px', background:'#fef3c7', color:'#d97706', borderRadius:'999px', padding:'2px 8px', fontWeight:700 }}>{modCount} edited</span>}
        <span style={{ fontSize:'10px', color:'#9ca3af' }}>{collapsed?'▶':'▼'}</span>
      </button>
      {!collapsed && (
        <div style={ isConsentGroup ? {} : { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px, 1fr))', gap:'0 20px' }}>
          {fields.map(f => {
            const dv = editMap[f.fieldKey] !== undefined ? editMap[f.fieldKey] : f.value;
            const ov = originalMap[f.fieldKey]?.value;
            const mod = !f.readOnly && editMap[f.fieldKey] !== undefined && editMap[f.fieldKey] !== ov;
            return <FieldRow key={f.fieldKey} field={{...f,value:dv}} originalField={originalMap[f.fieldKey]} onChange={onChange} isModified={mod} />;
          })}
        </div>
      )}
    </div>
  );
}

// ── DependentsSection ─────────────────────────────────────────────────────────
function DependentsSection({ allFields, editMap, deletedKeys, onChange, onDelete, onAddDependent }) {
  const [collapsed, setCollapsed] = useState(false);
  const depNums = new Set();
  allFields.forEach(f => { const m = f.fieldKey.match(/^dependent(\d+)/); if (m) depNums.add(Number(m[1])); });
  Object.keys(editMap).forEach(k => { const m = k.match(/^dependent(\d+)/); if (m) depNums.add(Number(m[1])); });
  const visible = [...depNums].sort((a,b)=>a-b).filter(n => !depFields(n).map(f=>f.key).every(k => deletedKeys.has(k)));
  return (
    <div style={{ marginBottom:'22px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'5px 0 8px', borderBottom:'2px solid #f3f4f6', marginBottom:'12px' }}>
        <button onClick={()=>setCollapsed(p=>!p)} style={{ display:'flex', alignItems:'center', gap:'8px', flex:1, background:'none', border:'none', cursor:'pointer', padding:0 }}>
          <span style={{ fontSize:'14px' }}>👨‍👩‍👧</span>
          <span style={{ fontSize:'11px', fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em' }}>Dependents</span>
          <span style={{ fontSize:'10px', color:'#9ca3af', marginLeft:'4px' }}>{collapsed?'▶':'▼'}</span>
        </button>
        <button onClick={onAddDependent} style={{ fontSize:'11px', background:'#d1fae5', color:'#059669', border:'none', borderRadius:'6px', padding:'3px 10px', fontWeight:700, cursor:'pointer' }}>+ Add Dependent</button>
      </div>
      {!collapsed && (
        <div>
          {visible.length === 0 && <div style={{ color:'#9ca3af', fontSize:'13px', paddingBottom:'8px' }}>No dependents on file.</div>}
          {visible.map(n => (
            <div key={n} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'10px', padding:'14px 14px 6px', marginBottom:'14px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                <span style={{ fontSize:'11px', fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em' }}>Dependent {n}</span>
                <button onClick={()=>onDelete(depFields(n).map(f=>f.key))} style={{ background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', cursor:'pointer', fontWeight:700 }}>✕ Remove</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(185px, 1fr))', gap:'0 20px' }}>
                {depFields(n).map(({ key, label, sensitive }) => {
                  const fromData = allFields.find(f=>f.fieldKey===key);
                  const currentVal = editMap[key] !== undefined ? editMap[key] : (fromData?.value||'');
                  const mod = editMap[key] !== undefined && editMap[key] !== (fromData?.value||'');
                  return <FieldRow key={key} field={{ fieldKey:key, fieldLabel:label, value:currentVal, readOnly:false, group:'Dependents', sensitive:!!sensitive }} originalField={fromData} onChange={onChange} isModified={mod} />;
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── SpouseSection ─────────────────────────────────────────────────────────────
function SpouseSection({ allFields, editMap, deletedKeys, onChange, onDelete, onAddSpouse }) {
  const [collapsed, setCollapsed] = useState(false);
  const keys = SPOUSE_FIELDS.map(f=>f.key);
  const hasAny = keys.some(k => (allFields.find(f=>f.fieldKey===k)?.value) || editMap[k] !== undefined);
  const allDeleted = keys.every(k=>deletedKeys.has(k));
  const showForm = hasAny && !allDeleted;
  return (
    <div style={{ marginBottom:'22px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'5px 0 8px', borderBottom:'2px solid #f3f4f6', marginBottom:'12px' }}>
        {showForm ? (
          <button onClick={()=>setCollapsed(p=>!p)} style={{ display:'flex', alignItems:'center', gap:'8px', flex:1, background:'none', border:'none', cursor:'pointer', padding:0 }}>
            <span style={{ fontSize:'14px' }}>💍</span>
            <span style={{ fontSize:'11px', fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em' }}>Spouse</span>
            <span style={{ fontSize:'10px', color:'#9ca3af', marginLeft:'4px' }}>{collapsed?'▶':'▼'}</span>
          </button>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
            <span style={{ fontSize:'14px' }}>💍</span>
            <span style={{ fontSize:'11px', fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em' }}>Spouse</span>
          </div>
        )}
        {!showForm && (
          <button onClick={onAddSpouse} style={{ fontSize:'11px', background:'#d1fae5', color:'#059669', border:'none', borderRadius:'6px', padding:'3px 10px', fontWeight:700, cursor:'pointer' }}>+ Add Spouse</button>
        )}
      </div>
      {!showForm && <div style={{ color:'#9ca3af', fontSize:'13px', paddingBottom:'8px' }}>No spouse on file.</div>}
      {showForm && !collapsed && (
        <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'10px', padding:'14px 14px 6px' }}>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'6px' }}>
            <button onClick={()=>onDelete(keys)} style={{ background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', cursor:'pointer', fontWeight:700 }}>✕ Remove Spouse</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(185px, 1fr))', gap:'0 20px' }}>
            {SPOUSE_FIELDS.map(({ key, label, sensitive }) => {
              const fromData = allFields.find(f=>f.fieldKey===key);
              const currentVal = editMap[key] !== undefined ? editMap[key] : (fromData?.value||'');
              const mod = editMap[key] !== undefined && editMap[key] !== (fromData?.value||'');
              return <FieldRow key={key} field={{ fieldKey:key, fieldLabel:label, value:currentVal, readOnly:false, group:'Spouse', sensitive:!!sensitive }} originalField={fromData} onChange={onChange} isModified={mod} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}


